import { NumberUtils } from "@/utils/NumberUtils";
import { Card } from "@tremor/react";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, Rectangle } from "recharts";
import { MultiLineTick, formatWeekLabel } from "./StackBarChart";
import { MonthYearTick } from "./components/MonthYearTick";
import { IBarChartWidget } from "@/modules/widgets/types/BarChartWidget";
import classNames from "classnames";
import { BarChartCompare } from "./BarChartCompare";
import { useWidgetFiltersController } from "../WidgetFilters/useWidgetFiltersController";
import { NoDataView } from "../NoDataView";

const AngledTick = ({ x, y, payload, maxLength = 15 }: any) => {
  const text = payload.value || '';
  const truncated = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill="#5a717a"
        fontSize={12}
        fontFamily="Roboto, sans-serif"
        transform="rotate(-45)"
      >
        <title>{text}</title>
        {truncated}
      </text>
    </g>
  );
};

interface CompareData {
  unified: {
    data: Array<{
      identifiers: {
        segment: string;
      };
      current?: Record<string, number>;
      previous?: Record<string, number>;
    }>;
  };
}

interface IBarChartProps {
  rightTotalLegends?: boolean;
  data: IBarChartWidget['data'];
  colors?: string[];
  compare?: CompareData;
  selectedCompareEnabled?: boolean;
  metadata?: IBarChartWidget['metadata'];
  colSpan?: number;
  segment?: string;
  hiddenSeries?: Set<string>;
  widgetFiltersController?: ReturnType<typeof useWidgetFiltersController>;
}

const HoverCursor = (props: any) => {
  const { x, y, width, height } = props;

  if (x == null || width == null || y == null || height == null) return null;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill="rgba(148, 163, 184, 0.18)" 
      radius={4}
    />
  );
};

const BarChart: React.FC<IBarChartProps> = ({ rightTotalLegends = false, data, colors, compare, selectedCompareEnabled, metadata, colSpan, segment, hiddenSeries, widgetFiltersController }) => {
  const hiddenMetrics = hiddenSeries ?? new Set<string>();
  const [hiddenSegments, setHiddenSegments] = useState(new Set<string>());
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const defaultColors = ['#341cac', '#429795', '#84c4c4', '#6b46c1'];
  const chartColors = colors && colors.length > 0 ? colors : defaultColors;

  // Observe container width changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);


  const filteredBaseData = useMemo(() => {
    const base = data.data || [];
    const filters = (data as any)?.metadata?.filters;

    if (!widgetFiltersController || !Array.isArray(filters) || filters.length === 0) {
      return base;
    }

    const { selectedFilters } = widgetFiltersController;

    return base.filter((row: any) => {
      for (const filterConfig of filters) {
        const metric = filterConfig?.metric;
        const items = filterConfig?.items;
        if (!metric || !Array.isArray(items) || items.length === 0) continue;

        const selectedItems = selectedFilters?.[metric] || [];
        if (selectedItems.length >= items.length) continue;

        const datasetMetric = metric === 'segmentValue' ? 'segment' : metric;
        const rowValue = row?.[datasetMetric];

        const matches = selectedItems.some((item: any) => String(rowValue) === item.text);
        if (!matches) return false;
      }
      return true;
    });
  }, [data, widgetFiltersController]);


  if (selectedCompareEnabled && compare) {
    return <BarChartCompare rightTotalLegends={rightTotalLegends} data={data} compare={compare} colors={colors} metadata={metadata} />;
  }

  if (data.error) {
    return <div className="py-5 px-10  text-center text-red-500">Error: {data.error}</div>
  }



  // Determine X-axis label orientation based on container width and number of data points
  const dataPointCount = filteredBaseData.length || 0;
  const shouldAngleLabels = containerWidth > 0 && (
    (containerWidth < 600 && dataPointCount > 4) ||
    (containerWidth < 800 && dataPointCount > 6) ||
    (dataPointCount > 8)
  );

  const toggleMetric = (_metricName: string) => {
    // Visibility is controlled externally via hiddenSeries prop
  }

  const toggleSegment = (segment: string) => {
    setHiddenSegments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(segment)) {
        newSet.delete(segment);
      } else {
        newSet.add(segment);
      }
      return newSet;
    });
  }

  const metrics = data.metrics.filter(metric => !hiddenMetrics.has(metric.metricName)).sort((a, b) => {
    return data.metrics.findIndex(metric => metric.metricName === a.metricName) - data.metrics.findIndex(metric => metric.metricName === b.metricName)
  })

  const metricMaxValues = metrics.map(metric => {
    const values = filteredBaseData.map(item => (item as any)[metric.metricName] as number);
    return {
      metricName: metric.metricName,
      showName: metric.showName,
      max: Math.max(...values),
      avg: values.reduce((a, b) => a + b, 0) / values.length
    };
  });

  // Find the metric with the largest average value (reference metric)
  const referenceMetric = metricMaxValues.reduce((prev, curr) =>
    curr.avg > prev.avg ? curr : prev,
    { avg: 0, max: 0, metricName: "", showName: "" }
  );

  // Calculate scaling factors for metrics that are significantly smaller
  const scalingFactors = metricMaxValues.map(metric => {
    const ratio = referenceMetric.avg / metric.avg;
    // If a metric is more than 100x smaller, scale it up
    const needsScaling = ratio > 100;
    return {
      metricName: metric.metricName,
      showName: metric.showName,
      scaleFactor: needsScaling ? Math.round(ratio / 10) : 1,
      needsScaling
    };
  });

  // Create a color mapping based on original data order to maintain consistency
  const segmentColorMap = new Map<string, number>();
  filteredBaseData.forEach((dataPoint, index) => {
    segmentColorMap.set(dataPoint.segment, index % chartColors.length);
  });

  const hasAnyNonZeroValue = filteredBaseData.some(item =>
    data.metrics.some(metric => {
      const value = Number((item as any)[metric.metricName]) || 0;
      return value !== 0;
    })
  );

  // Transform data to include scaled metrics and apply bar ordering if specified
  const transformedData = filteredBaseData
    .map(item => {
      const transformed: any = { ...item };
      scalingFactors.forEach(({ metricName, scaleFactor, needsScaling }) => {
        if (needsScaling) {
          transformed[`${metricName}Scaled`] = (item as any)[metricName] * scaleFactor;
          transformed[`${metricName}Original`] = (item as any)[metricName];
        }
      });
      return transformed;
    })
    .filter(item => {
      if (metadata?.showZeroBars && hasAnyNonZeroValue) return true;
      return data.metrics.some(metric => {
        const value = Number((item as any)[metric.metricName]) || 0;
        return value !== 0;
      });
    })
    .filter(item => {
      // Filter out hidden segments when there's only one metric
      if (metrics.length === 1) {
        return !hiddenSegments.has(item.segment);
      }
      return true;
    })
    .sort((a, b) => {
      // Apply bar ordering if metadata specifies it
      if (metadata?.barOrdering) {
        const sortMetric = (metadata as any).barOrderingMetric
          ? metrics.find(m => m.metricName === (metadata as any).barOrderingMetric) ?? metrics[0]
          : metrics[0];
        const aValue = (a as any)[sortMetric.metricName] || 0;
        const bValue = (b as any)[sortMetric.metricName] || 0;

        if (metadata.barOrdering === 'asc') {
          return aValue - bValue;
        } else if (metadata.barOrdering === 'desc') {
          return bValue - aValue;
        }
      }
      // For month segments, sort chronologically (e.g. "January 2026" → "April 2026")
      if (segment === 'month') {
        const aTime = new Date(a.segment).getTime();
        const bTime = new Date(b.segment).getTime();
        if (!isNaN(aTime) && !isNaN(bTime)) return aTime - bTime;
      }
      // Default: keep original order
      return 0;
    });

  const getTotalMetric = (metricName: string) => {
    const values = transformedData.map(item => (item as any)[metricName] as number);
    const total = values.reduce((a, b) => a + b, 0);
    return NumberUtils.formatCompact(total)
  };

  // Check if legends should be hidden via metadata
  const hideLegends = metadata?.chart?.hideLegends === true;

  // Nothing left to draw after filtering zero-value rows — show an explicit
  // empty state instead of an empty Recharts canvas (which renders as a
  // dashed grid rectangle and confuses users).
  if (transformedData.length === 0) {
    return (
      <Card ref={containerRef} className="w-full mx-auto pt-1 px-6 pb-6 flex flex-col items-center justify-center min-h-[400px] h-full">
        <NoDataView />
      </Card>
    );
  }

  return (
    <Card ref={containerRef} className="w-full mx-auto pt-1 px-6 pb-6 flex flex-col min-h-[400px] h-full">
      {!rightTotalLegends && !hideLegends && (
        <ul className="flex justify-start gap-5 text-primary-blue list-none p-0 mb-6 ml-2 flex-wrap">
          {metrics.length === 1 ? (
            // Show segments as legend items when there's only one metric
            [...filteredBaseData]
              .sort((a, b) => {
                if (segment === 'month') {
                  const aTime = new Date(a.segment).getTime();
                  const bTime = new Date(b.segment).getTime();
                  if (!isNaN(aTime) && !isNaN(bTime)) return aTime - bTime;
                }
                return 0;
              })
              .map((dataPoint) => {
                const colorIndex = segmentColorMap.get(dataPoint.segment) ?? 0;
                return (
                  <li
                    key={dataPoint.segment}
                    onClick={() => toggleSegment(dataPoint.segment)}
                    className={classNames("flex gap-2 items-center font-normal cursor-pointer", hiddenSegments.has(dataPoint.segment) ? "opacity-50" : "")}
                  >
                    <span
                      className="w-3 h-3 rounded-[3px] inline-block"
                      style={{
                        backgroundColor: chartColors[colorIndex],
                      }}
                    />
                    <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{dataPoint.segment}</span>
                  </li>
                );
              })
          ) : (
            // Show metrics as legend items when there are multiple metrics
            data.metrics.map((metric, i) => (
              <li key={metric.metricName} onClick={() => toggleMetric(metric.metricName)} className={classNames("flex gap-2 items-center font-normal cursor-pointer", hiddenMetrics.has(metric.metricName) ? "opacity-50" : "")}>
                <span
                  className="w-3 h-3 rounded-[3px] inline-block"
                  style={{
                    backgroundColor: chartColors[i % chartColors.length],
                  }}
                />
                <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{metric.showName}</span>
              </li>
            ))
          )}
        </ul>
      )
      }
      
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={transformedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            barGap={8}
            barCategoryGap="25%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={true}
              horizontal={true}
              stroke="#e5e7eb"
            />
            {/* X-axis */}
            <XAxis
              dataKey="segment"
              interval={0}
              height={shouldAngleLabels ? 80 : 60}
              tick={shouldAngleLabels ? <AngledTick /> : (props) => {
                if (segment === 'month') {
                  return (
                    <MonthYearTick
                      {...props}
                      fontSize={12}
                      lineHeight={14}
                    />
                  );
                }
                const isWeek = segment === 'week';
                const tickProps = isWeek
                  ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } }
                  : props;
                return (
                  <MultiLineTick
                    {...tickProps}
                    maxCharsPerLine={isWeek ? 30 : 12}
                    maxLines={2}
                    fontSize={12}
                    lineHeight={14}
                  />
                );
              }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />

            {/* Single Y-axis */}
            <YAxis
              tick={{ fill: '#5a717a', fontSize: 12, fontFamily: 'Roboto, sans-serif', opacity: 1 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
              tickFormatter={NumberUtils.formatCompact}
            />

            {/* Tooltip + custom hover cursor overlay */}
            <Tooltip
              cursor={<HoverCursor />}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;

                return (
                  <div
                    className="bg-white border border-gray-200 rounded-lg shadow-lg p-4"
                  >
                    {/* Label/Date */}
                    <div
                      className="font-normal mb-8px"
                    >
                      {label}
                    </div>

                    {/* Metrics */}
                    {payload.map((entry: any, index: number) => {
                      const scalingInfo = scalingFactors.find(sf => sf.showName === entry.name);
                      let displayValue = entry.value;

                      if (scalingInfo?.needsScaling) {
                        displayValue = entry.payload[`${scalingInfo.metricName}Original`];
                      }

                      const metricDef = data.metrics.find(m => m.showName === entry.name || m.metricName === entry.dataKey);
                      const formattedValue = metricDef?.metricType === 'percent'
                        ? NumberUtils.formatPercent(NumberUtils.round(displayValue))
                        : metricDef?.metricType === 'currency'
                          ? NumberUtils.formatCurrency(NumberUtils.round(displayValue))
                          : NumberUtils.formatStandard(NumberUtils.round(displayValue));

                      return (
                        <div
                          key={index}
                          className="flex items-center gap-8px"
                        >

                          <div className="flex items-center gap-2  text-sm mt-2">
                            <div
                              className="flex items-center gap-2"
                            >
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor: metrics.length === 1 ? (segmentColorMap.get(String(label)) !== undefined ? chartColors[segmentColorMap.get(String(label))!] : chartColors[0]) : entry.color,
                                  flexShrink: 0
                                }}
                              />
                              {entry.name}:
                            </div>
                            <div>
                              {formattedValue}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />

            {/* Bars - dynamically render based on metrics */}
            {metrics.map((metric, i) => {
              const scalingInfo = scalingFactors.find(sf => sf.metricName === metric.metricName);
              const dataKey = scalingInfo?.needsScaling
                ? `${metric.metricName}Scaled`
                : metric.metricName;

              return (
                <Bar
                  key={metric.metricName + '-' + i}
                  dataKey={dataKey}
                  name={metric.showName}
                  fill={chartColors[data.metrics.findIndex(_metric => metric.metricName === _metric.metricName) % chartColors.length]}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={60}
                  shape={(props: any) => {
                    if (metrics.length === 1) {
                      const colorIndex = segmentColorMap.get(props.segment);
                      const color = colorIndex !== undefined ? chartColors[colorIndex] : chartColors[0];
                      return <Rectangle {...props} fill={color} />;
                    }
                    return <Rectangle {...props} />;
                  }}
                />
              );
            })}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      {/* Summary Cards - on the right side on desktop, below chart on mobile */}
      {
        data.metrics && rightTotalLegends && (
          <div className="mt-6 lg:mt-0 lg:w-[180px] lg:flex-shrink-0 overflow-hidden">
            <ul
              role="list"
              className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 max-h-96 overflow-y-auto"
            >
              {data.metrics.map((metric, index) => {
                // Get the color for this region
                const color = chartColors[index % chartColors.length];

                return (
                  <li
                    key={metric.metricName}
                    className="rounded-tremor-small border border-gray-300 rounded px-3 py-2 text-left dark:border-dark-tremor-border w-full max-w-11/12"
                    style={{ height: '58px' }}
                  >
                    <div className="flex items-center space-x-1.5">
                      {/* Only show color indicators when there are multiple categories */}
                      {data.metrics.length > 1 && (
                        <span
                          className="size-2.5 rounded-sm"
                          style={{ backgroundColor: color }}
                          aria-hidden={true}
                        />
                      )}
                      <p
                        className="truncate"
                        style={{
                          color: '#2f363f',
                          fontSize: '12px',
                          fontFamily: 'Roboto, sans-serif',
                          maxWidth: data.metrics.length > 1 ? '100px' : '120px', // Give more space when no color indicator
                        }}
                        title={metric.showName} // Show full name on hover
                      >
                        {metric.showName}
                      </p>
                    </div>
                    <p
                      className="mt-0.5 font-semibold"
                      style={{
                        color: '#2f363f',
                        fontSize: '12px',
                        fontFamily: 'Roboto, sans-serif'
                      }}
                    >
                      {getTotalMetric(metric.metricName)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )
      }
    </Card >
  );
}
export { BarChart }